import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';

const GuidedResponse = ({ draft, siblingDrafts = [], onSave, onBack }) => {
  // Phase: 'discovery' | 'synthesis' | 'refine'
  const [phase, setPhase] = useState('discovery');

  // Discovery state
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [discoveryResponses, setDiscoveryResponses] = useState([]);
  const [currentAnswer, setCurrentAnswer] = useState('');

  // Synthesis state
  const [aiDraft, setAiDraft] = useState('');
  const [synthesisLoading, setSynthesisLoading] = useState(false);
  const [steeringInput, setSteeringInput] = useState('');
  const [revisionCount, setRevisionCount] = useState(0);

  // Refine state
  const [editableDraft, setEditableDraft] = useState('');
  const [showDiscovery, setShowDiscovery] = useState(true);

  // Auto-save
  const saveTimeoutRef = useRef(null);

  // Load existing discovery data if resuming
  useEffect(() => {
    if (draft.builderData?.discoveryResponses?.length > 0) {
      setDiscoveryResponses(draft.builderData.discoveryResponses);
      if (draft.builderData.questions?.length > 0) {
        setQuestions(draft.builderData.questions);
        setQuestionsLoading(false);
      }
      if (draft.builderData.aiDraft) {
        setAiDraft(draft.builderData.aiDraft);
        setEditableDraft(draft.builderData.aiDraft);
      }
      // Resume at appropriate phase
      if (draft.content && draft.status === 'complete') {
        setEditableDraft(draft.content);
        setPhase('refine');
      } else if (draft.builderData.aiDraft) {
        setPhase('synthesis');
      } else if (draft.builderData.discoveryResponses.length >= (draft.builderData.questions?.length || 3)) {
        setPhase('synthesis');
      } else {
        setCurrentQuestionIndex(draft.builderData.discoveryResponses.length);
      }
    }
  }, []);

  // Generate discovery questions on mount
  useEffect(() => {
    if (questions.length === 0) {
      generateQuestions();
    }
  }, []);

  const generateQuestions = async () => {
    setQuestionsLoading(true);
    try {
      const response = await fetch('/api/guided-response/generate-questions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          promptText: draft.promptText,
          programName: draft.programName || null,
          wordLimit: draft.wordLimit || null,
          contextNotes: draft.contextNotes || null,
          siblingPrompts: siblingDrafts.map(s => ({
            promptText: s.promptText,
            content: s.content || null
          }))
        })
      });

      const data = await response.json();
      if (data.success && data.questions?.length > 0) {
        setQuestions(data.questions);
        persistState({ questions: data.questions });
      } else {
        throw new Error('No questions returned');
      }
    } catch (error) {
      console.error('Error generating questions:', error);
      // Use fallback questions
      const fallback = [
        { question: "Forget the application for a second. When was the last time something related to this genuinely surprised or puzzled you?", purpose: "Authentic curiosity" },
        { question: "What's something about this you're curious about that you can't explore just by reading or watching videos?", purpose: "Hands-on desire" },
        { question: "If this experience went perfectly, what would you understand or be able to do that you can't right now?", purpose: "Specific growth" }
      ];
      setQuestions(fallback);
      persistState({ questions: fallback });
    } finally {
      setQuestionsLoading(false);
    }
  };

  const persistState = useCallback((updates = {}) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      const currentState = {
        mode: 'guided-response',
        questions,
        discoveryResponses,
        aiDraft,
        ...updates
      };
      onSave({
        builderData: currentState,
        content: editableDraft || aiDraft || '',
        wordCount: (editableDraft || aiDraft || '').trim().split(/\s+/).filter(w => w).length,
        status: phase === 'refine' ? 'in_progress' : 'in_progress'
      });
    }, 1000);
  }, [questions, discoveryResponses, aiDraft, editableDraft, phase, onSave]);

  // === DISCOVERY PHASE ===

  const handleSubmitAnswer = () => {
    if (!currentAnswer.trim()) return;

    const newResponse = {
      question: questions[currentQuestionIndex]?.question || '',
      answer: currentAnswer.trim()
    };
    const updated = [...discoveryResponses, newResponse];
    setDiscoveryResponses(updated);
    setCurrentAnswer('');

    persistState({ discoveryResponses: updated });

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handleStartSynthesis = async () => {
    setPhase('synthesis');
    await synthesizeDraft(discoveryResponses);
  };

  const allQuestionsAnswered = discoveryResponses.length >= questions.length && questions.length > 0;

  // === SYNTHESIS PHASE ===

  const synthesizeDraft = async (responses, feedback = null, previous = null) => {
    setSynthesisLoading(true);
    try {
      const response = await fetch('/api/guided-response/synthesize-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          promptText: draft.promptText,
          discoveryResponses: responses,
          programName: draft.programName || null,
          wordLimit: draft.wordLimit || null,
          contextNotes: draft.contextNotes || null,
          siblingDrafts: siblingDrafts.map(s => ({
            promptText: s.promptText,
            content: s.content || null
          })),
          steeringFeedback: feedback,
          previousDraft: previous
        })
      });

      const data = await response.json();
      if (data.success && data.draft) {
        setAiDraft(data.draft);
        setEditableDraft(data.draft);
        persistState({ aiDraft: data.draft });
        if (feedback) {
          setRevisionCount(prev => prev + 1);
          toast.success('Draft revised!');
        }
      } else {
        throw new Error(data.error || 'Failed to generate draft');
      }
    } catch (error) {
      console.error('Error synthesizing draft:', error);
      toast.error('Failed to generate draft. Please try again.');
    } finally {
      setSynthesisLoading(false);
    }
  };

  const handleSteer = () => {
    if (!steeringInput.trim()) return;
    synthesizeDraft(discoveryResponses, steeringInput.trim(), aiDraft);
    setSteeringInput('');
  };

  const handleStartEditing = () => {
    setEditableDraft(aiDraft);
    setPhase('refine');
  };

  // === REFINE PHASE ===

  const handleDraftChange = (value) => {
    setEditableDraft(value);
    persistState();
  };

  const handleMarkComplete = () => {
    onSave({
      builderData: {
        mode: 'guided-response',
        questions,
        discoveryResponses,
        aiDraft
      },
      content: editableDraft,
      wordCount: editableDraft.trim().split(/\s+/).filter(w => w).length,
      status: 'complete'
    });
    toast.success('Response saved!');
    onBack();
  };

  const wordCount = (editableDraft || aiDraft || '').trim().split(/\s+/).filter(w => w).length;
  const wordLimitDisplay = draft.wordLimit ? ` / ${draft.wordLimit}` : '';

  // === RENDER ===

  return (
    <div>
      {/* Header */}
      <div className="bg-violet-50 border-b border-violet-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <p className="text-xs text-violet-600 font-medium mb-0.5">
              {phase === 'discovery' ? 'Discovery' : phase === 'synthesis' ? 'Draft Synthesis' : 'Refine Your Response'}
            </p>
            <p className="text-sm text-slate-800 truncate">{draft.promptText}</p>
          </div>
          <div className="flex items-center gap-3 shrink-0 ml-3">
            <div className="text-xs text-stone-500">
              {wordCount}{wordLimitDisplay} words
            </div>
            <button onClick={onBack} className="text-xs text-violet-600 hover:text-violet-800 font-medium">
              Back to List
            </button>
          </div>
        </div>
      </div>

      {/* Phase indicator */}
      <div className="px-4 py-2 bg-white border-b border-stone-200">
        <div className="flex items-center gap-1 text-xs">
          {['discovery', 'synthesis', 'refine'].map((p, i) => (
            <React.Fragment key={p}>
              {i > 0 && <span className="text-stone-300 mx-1">&rarr;</span>}
              <span className={`px-2 py-1 rounded-full font-medium ${
                phase === p
                  ? 'bg-violet-100 text-violet-700'
                  : discoveryResponses.length > 0 && i === 0 ? 'text-emerald-600'
                  : aiDraft && i <= 1 ? 'text-emerald-600'
                  : 'text-stone-400'
              }`}>
                {p === 'discovery' ? 'Discover' : p === 'synthesis' ? 'Draft' : 'Refine'}
              </span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* === DISCOVERY PHASE === */}
      {phase === 'discovery' && (
        <div className="p-4 max-w-2xl mx-auto">
          {questionsLoading ? (
            <div className="text-center py-12">
              <div className="animate-pulse text-violet-600 text-sm mb-2">Thinking about your prompt...</div>
              <p className="text-xs text-stone-500">Generating questions tailored to this application</p>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <p className="text-sm text-stone-600 mb-1">
                  Before you write, let's figure out what you actually want to say. Answer these honestly — there are no wrong answers.
                </p>
                {draft.contextNotes && (
                  <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs font-medium text-amber-700 mb-1">Your Notes</p>
                    <p className="text-xs text-amber-800 whitespace-pre-wrap line-clamp-4">{draft.contextNotes}</p>
                  </div>
                )}
                {siblingDrafts.length > 0 && (
                  <div className="mt-2 bg-sky-50 border border-sky-200 rounded-lg p-3">
                    <p className="text-xs font-medium text-sky-700 mb-1">
                      Other questions in this application ({siblingDrafts.length})
                    </p>
                    {siblingDrafts.map((s, i) => (
                      <p key={s.id} className="text-xs text-sky-800 truncate">
                        {i + 1}. {s.promptText}
                        {s.content ? ' ✓' : ''}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              {/* Previous responses */}
              {discoveryResponses.map((r, i) => (
                <div key={i} className="mb-4">
                  <p className="text-sm font-medium text-slate-700 mb-1">{r.question}</p>
                  <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-sm text-slate-800">
                    {r.answer}
                  </div>
                </div>
              ))}

              {/* Current question */}
              {!allQuestionsAnswered && questions[currentQuestionIndex] && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-slate-800 mb-2">
                    {questions[currentQuestionIndex].question}
                  </p>
                  <textarea
                    value={currentAnswer}
                    onChange={(e) => setCurrentAnswer(e.target.value)}
                    placeholder="Just talk naturally — this isn't the essay, it's brainstorming..."
                    rows={4}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent resize-none"
                    autoFocus
                  />
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-stone-400">
                      {currentAnswer.trim().split(/\s+/).filter(w => w).length} words
                    </p>
                    <button
                      onClick={handleSubmitAnswer}
                      disabled={currentAnswer.trim().split(/\s+/).filter(w => w).length < 5}
                      className="px-4 py-1.5 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                    >
                      {currentQuestionIndex < questions.length - 1 ? 'Next Question' : 'Done'}
                    </button>
                  </div>
                </div>
              )}

              {/* Ready to synthesize */}
              {allQuestionsAnswered && (
                <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                  <p className="text-sm text-emerald-800 font-medium mb-2">
                    Great material. Ready to build your draft?
                  </p>
                  <p className="text-xs text-emerald-600 mb-3">
                    We'll weave your responses into a cohesive answer that sounds like you.
                  </p>
                  <button
                    onClick={handleStartSynthesis}
                    className="px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700"
                  >
                    Build My Draft
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* === SYNTHESIS PHASE === */}
      {phase === 'synthesis' && (
        <div className="p-4">
          {synthesisLoading ? (
            <div className="text-center py-12 max-w-md mx-auto">
              <div className="animate-pulse mb-3">
                <div className="w-8 h-8 bg-violet-200 rounded-full mx-auto mb-2" />
              </div>
              <p className="text-sm text-violet-700 font-medium mb-1">
                {revisionCount > 0 ? 'Revising your draft...' : 'Building your draft...'}
              </p>
              <p className="text-xs text-stone-500">
                Weaving your reflections into a cohesive response
              </p>
            </div>
          ) : aiDraft ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Draft */}
              <div className="lg:col-span-2">
                <div className="bg-white border border-stone-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-900">
                      Your Draft {revisionCount > 0 ? `(Revision ${revisionCount})` : ''}
                    </h3>
                    <span className="text-xs text-stone-500">
                      {aiDraft.trim().split(/\s+/).filter(w => w).length}{wordLimitDisplay} words
                    </span>
                  </div>
                  <div className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap bg-stone-50 rounded-lg p-4 border border-stone-100">
                    {aiDraft}
                  </div>
                </div>

                {/* Steering */}
                <div className="mt-3 bg-white border border-stone-200 rounded-xl p-4">
                  <p className="text-sm font-medium text-slate-700 mb-2">
                    What would you change?
                  </p>
                  <textarea
                    value={steeringInput}
                    onChange={(e) => setSteeringInput(e.target.value)}
                    placeholder={'e.g. "Emphasize the part about my experience at the wildlife center" or "This sounds too formal"'}
                    rows={2}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent resize-none mb-2"
                  />
                  <div className="flex items-center justify-between">
                    <button
                      onClick={handleSteer}
                      disabled={!steeringInput.trim()}
                      className="px-4 py-1.5 text-sm border border-violet-300 text-violet-700 rounded-lg hover:bg-violet-50 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                    >
                      Revise Draft
                    </button>
                    <button
                      onClick={handleStartEditing}
                      className="px-4 py-1.5 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 font-medium"
                    >
                      Start Editing
                    </button>
                  </div>
                </div>
              </div>

              {/* Discovery sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-stone-50 border border-stone-200 rounded-xl p-3">
                  <h4 className="text-xs font-semibold text-stone-600 uppercase tracking-wide mb-2">Your Reflections</h4>
                  {discoveryResponses.map((r, i) => (
                    <div key={i} className="mb-3 last:mb-0">
                      <p className="text-xs text-stone-500 mb-0.5">{r.question}</p>
                      <p className="text-xs text-slate-700">{r.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* === REFINE PHASE === */}
      {phase === 'refine' && (
        <div className="p-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Editor */}
            <div className="lg:col-span-2">
              <div className="bg-white border border-stone-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-900">Edit Your Response</h3>
                  <span className={`text-xs font-medium ${
                    draft.wordLimit && wordCount > draft.wordLimit ? 'text-red-600' : 'text-stone-500'
                  }`}>
                    {wordCount}{wordLimitDisplay} words
                  </span>
                </div>
                <textarea
                  value={editableDraft}
                  onChange={(e) => handleDraftChange(e.target.value)}
                  rows={12}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent resize-y leading-relaxed"
                  autoFocus
                />
                {draft.wordLimit && wordCount > draft.wordLimit && (
                  <p className="mt-1 text-xs text-red-600">
                    {wordCount - draft.wordLimit} words over the limit
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between mt-3">
                <button
                  onClick={() => setPhase('synthesis')}
                  className="text-sm text-violet-600 hover:text-violet-800 font-medium"
                >
                  &larr; Back to AI Draft
                </button>
                <button
                  onClick={handleMarkComplete}
                  className="px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700"
                >
                  Save Response
                </button>
              </div>
            </div>

            {/* Discovery reference */}
            <div className="lg:col-span-1">
              <div className="sticky top-4">
                <button
                  onClick={() => setShowDiscovery(!showDiscovery)}
                  className="text-xs font-semibold text-stone-600 uppercase tracking-wide mb-2 flex items-center gap-1 hover:text-stone-800"
                >
                  Your Reflections {showDiscovery ? '(hide)' : '(show)'}
                </button>
                {showDiscovery && (
                  <div className="bg-stone-50 border border-stone-200 rounded-xl p-3">
                    {discoveryResponses.map((r, i) => (
                      <div key={i} className="mb-3 last:mb-0">
                        <p className="text-xs text-stone-500 mb-0.5">{r.question}</p>
                        <p className="text-xs text-slate-700">{r.answer}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuidedResponse;
